import { NavLink, Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import api, { setScoutingPin, setMedicalPin, setDirectorPin, setAdminPin, setAnalysisPin } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canAccessArea } from "@/lib/areas";
import { planAllows, planOf, PLAN_FEATURES } from "@/lib/plan";
import { useScoutingUnlocked } from "@/components/ScoutingGate";
import { useMedicalUnlocked } from "@/components/MedicalGate";
import { useDirectorUnlocked } from "@/components/DirectorGate";
import { useAdminUnlocked } from "@/components/AdminGate";
import { useAnalysisUnlocked } from "@/components/AnalysisGate";
import { Button } from "@/components/ui/button";
import LanguageToggle from "@/components/LanguageToggle";
import { useLang } from "@/context/LanguageContext";
import { House, Users, Eye, Target, Trophy, Strategy, VideoCamera, CalendarCheck, CalendarBlank, SignOut, LockKey, LockKeyOpen, Heartbeat, ShieldCheck, Crosshair, Bank, ChartBar, GearSix, Crown, SoccerBall, Scales, Pulse, Headset, ArrowsLeftRight, Function } from "@phosphor-icons/react";

const IT_NAV = {
  "Dashboard": "Dashboard",
  "Budget": "Budget",
  "Players Watchlist": "Da vedere",
  "Players rated": "Players rated",
  "Players to sign": "Da ingaggiare",
  "Leagues and Teams": "Campionati e squadre",
  "Leagues": "Campionati",
  "Competitions": "Competizioni",
  "Squad": "Rosa in costruzione",
  "Building roster": "Rosa in costruzione",
  "Scouting Balance": "Bilancio Scouting",
  "Player Analysis": "Analisi Giocatrice",
  "Team Analysis": "Analisi Squadra",
  "Season Calendar": "Calendario Stagione",
  "Viscidi Matrix": "Matrice di Viscidi",
  "Next Opponent": "Prossima Avversaria",
  "Starting XI": "Starting XI",
  "Tactical Board": "Lavagna Tattica",
  "Training & Video": "Allenamenti & Video",
  "Medical Health": "Area Medica",
  "Wellness & RPE": "Wellness & RPE",
  "Access Control": "Controllo Accessi",
  "Clubs & Subscriptions": "Club & Abbonamenti",
  "Roster": "Rosa",
  "Staff": "Staff",
  "Budget": "Budget",
  "Settings": "Impostazioni",
};
const IT_SECTION = {
  "Hub": "Hub",
  "Sport Director": "Direttore Sportivo",
  "Scouting": "Scouting",
  "Head Coach & Analysis": "Allenatore & Analisi",
  "Medical": "Area Medica",
  "Admin": "Admin",
  "Platform": "Piattaforma",
};

export const NAV = [
  { section: "Hub", items: [{ to: "/", label: "Dashboard", icon: House, end: true }] },
  {
    section: "Sport Director",
    area: "director",
    items: [
      { to: "/direzione-sportiva?tab=rosa", label: "Roster", icon: Users, match: "/direzione-sportiva" },
      { to: "/direzione-sportiva?tab=staff", label: "Staff", icon: ShieldCheck, match: "/direzione-sportiva" },
      { to: "/direzione-sportiva?tab=budget", label: "Budget", icon: Bank, match: "/direzione-sportiva" },
    ],
  },
  {
    section: "Scouting",
    area: "scouting",
    items: [
      { to: "/da-vedere", label: "Players Watchlist", icon: Eye },
      { to: "/calciatrici", label: "Players rated", icon: Users },
      { to: "/da-ingaggiare", label: "Players to sign", icon: Target },
      { to: "/confronto-rosa", label: "Compare with squad", icon: ArrowsLeftRight },
      { to: "/campionati", label: "Leagues and Teams", icon: Trophy },
      { to: "/squad", label: "Building roster", icon: Strategy },
      { to: "/bilancio", label: "Scouting Balance", icon: Scales },
    ],
  },
  {
    section: "Head Coach & Analysis",
    area: "analysis",
    items: [
      { to: "/analysis/match", label: "Player Analysis", icon: VideoCamera },
      { to: "/analysis/team", label: "Team Analysis", icon: ChartBar },
      { to: "/analysis/calendar", label: "Season Calendar", icon: CalendarBlank },
      { to: "/analysis/viscidi", label: "Viscidi Matrix", icon: Function },
      { to: "/analysis/opponent", label: "Next Opponent", icon: Crosshair },
      { to: "/analysis/starting-xi", label: "Starting XI", icon: SoccerBall },
      { to: "/analysis/board", label: "Tactical Board", icon: Strategy },
      { to: "/analysis/training", label: "Training & Video", icon: CalendarCheck },
    ],
  },
  {
    section: "Medical",
    area: "medical",
    items: [
      { to: "/analysis/medical", label: "Medical Health", icon: Heartbeat },
      { to: "/analysis/wellness", label: "Wellness & RPE", icon: Pulse },
    ],
  },
];

export function PlanBadge({ className = "" }) {
  const { user } = useAuth();
  const plan = planOf(user);
  const label = PLAN_FEATURES[plan].label;
  const strong = plan !== "starter";
  return (
    <span
      data-testid="plan-badge"
      title={`Piano ${label}`}
      className={`inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${strong ? "bg-primary/20 text-primary border-primary/50" : "bg-white/10 text-white/70 border-white/20"} ${className}`}
    >
      {plan === "club" && <Crown size={9} weight="fill" />}{label}
    </span>
  );
}

export function Brand({ small = false }) {
  const { user } = useAuth();
  const org = user && typeof user === "object" ? user.org : null;
  const hasCrest = !!(org && org.crest_url);
  const crest = hasCrest
    ? (org.crest_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${org.crest_url}` : org.crest_url)
    : (org && org.is_default ? "/assets/crest_white.png" : "/assets/th_logo_dark.png");
  const sizeImg = small ? "w-8 h-8" : "w-11 h-11";
  const mark = <img src={crest} alt="Technical Hub" className={`${sizeImg} object-contain`} />;
  const wordmark = (
    <span className={`font-display font-black uppercase tracking-tight ${small ? "text-base" : "text-lg leading-none"}`}>
      Technical<span className="text-primary">Hub</span>
    </span>
  );
  if (small) {
    return (
      <Link to="/" data-testid="brand-home-link" className="flex items-center gap-2 transition-opacity hover:opacity-80" title="Dashboard">
        {mark}
        {wordmark}
        <PlanBadge />
      </Link>
    );
  }
  return (
    <Link to="/" data-testid="brand-home-link" className="flex items-center gap-3 px-6 py-6 border-b border-border transition-opacity hover:opacity-80" title="Dashboard">
      {mark}
      <span className="flex flex-col gap-1.5 items-start">
        {wordmark}
        <PlanBadge />
      </span>
    </Link>
  );
}

function SectionTitle({ section }) {
  const { tr } = useLang();
  const unlocked = useScoutingUnlocked();
  const medUnlocked = useMedicalUnlocked();
  const dirUnlocked = useDirectorUnlocked();
  const adminUnlocked = useAdminUnlocked();
  const anUnlocked = useAnalysisUnlocked();
  const LockToggle = ({ on, onLock, testId, color = "text-primary" }) => (
    on
      ? <button onClick={onLock} data-testid={`lock-${testId}`} title={`Lock ${section} area`} className="text-green-400 hover:text-red-400"><LockKeyOpen size={14} weight="fill" /></button>
      : <LockKey size={14} weight="fill" className={color} data-testid={`${testId}-locked-icon`} />
  );
  return (
    <p className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-[0.25em] font-bold text-white/55 flex items-center justify-between">
      {tr(IT_SECTION[section] || section, section)}
      {section === "Sport Director" && <LockToggle on={dirUnlocked} onLock={() => setDirectorPin("")} testId="director" />}
      {section === "Scouting" && <LockToggle on={unlocked} onLock={() => setScoutingPin("")} testId="scouting" />}
      {section === "Head Coach & Analysis" && <LockToggle on={anUnlocked} onLock={() => setAnalysisPin("")} testId="analysis" />}
      {section === "Medical" && <LockToggle on={medUnlocked} onLock={() => setMedicalPin("")} testId="medical" color="text-red-400" />}
      {section === "Admin" && <LockToggle on={adminUnlocked} onLock={() => setAdminPin("")} testId="admin" />}
    </p>
  );
}

export function NavMenu({ onNavigate }) {
  const { user } = useAuth();
  const { tr } = useLang();
  const location = useLocation();
  const [supportCount, setSupportCount] = useState(0);
  useEffect(() => {
    if (user?.platform_role !== "super_admin") return;
    let alive = true;
    const fetchCount = () => api.get("/support/unhandled-count").then((r) => { if (alive) setSupportCount(r.data?.count || 0); }).catch(() => {});
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [user]);
  const base = NAV.filter((g) => !g.area || canAccessArea(user, g.area));
  let groups = user?.is_admin ? [...base, { section: "Admin", items: [{ to: "/admin/users", label: "Access Control", icon: ShieldCheck }] }] : base;
  groups = [...groups, { section: "Hub", items: [{ to: "/impostazioni", label: "Settings", icon: GearSix }] }];
  if (user?.platform_role === "super_admin") {
    groups = [...groups, { section: "Platform", items: [{ to: "/superadmin", label: "Clubs & Subscriptions", icon: Bank }, { to: "/admin/support", label: "Support", icon: Headset }] }];
  }
  const currentTab = new URLSearchParams(location.search).get("tab");
  const slug = (to) => to.replace(/^\//, "").replace(/\?.*$/, "").replace(/\//g, "-") + (to.includes("tab=") ? "-" + to.split("tab=")[1] : "");
  return (
    <nav className="flex-1 py-4 px-3 space-y-4 overflow-y-auto">
      {groups.map((group, gi) => (
        <div key={`${group.section}-${gi}`} className="space-y-1">
          <SectionTitle section={group.section} />
          {group.items.map((item) => {
            const [itemPath, itemQuery] = item.to.split("?");
            const itemTab = itemQuery && itemQuery.includes("tab=") ? itemQuery.split("tab=")[1] : null;
            const locked = (item.to === "/analysis/board" && !planAllows(user, "tactical_board"))
              || (group.section === "Medical" && !planAllows(user, "medical"))
              || (group.area === "director" && !planAllows(user, "director"))
              || (item.to === "/da-ingaggiare" && !planAllows(user, "players_to_sign"))
              || (item.to === "/squad" && !planAllows(user, "squad"))
              || (item.to === "/bilancio" && !planAllows(user, "squad"));
            const isActive = itemTab
              ? location.pathname === itemPath && (currentTab === itemTab || (!currentTab && itemTab === "rosa"))
              : (item.end ? location.pathname === itemPath : location.pathname.startsWith(itemPath));
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.to === "/" ? "dashboard" : slug(item.to)}`}
                onClick={onNavigate}
                title={locked ? tr("Disponibile dal piano Pro", "Available from the Pro plan") : undefined}
                className={`flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-colors duration-200 border-l-2 ${
                  isActive ? "bg-primary/20 text-white font-semibold border-primary" : `border-transparent hover:bg-white/5 hover:text-foreground ${locked ? "text-muted-foreground/50" : "text-white/70"}`
                }`}
              >
                <item.icon size={20} weight="fill" />
                <span className="font-display uppercase tracking-wide text-[15px] flex-1">{tr(IT_NAV[item.label] || item.label, item.label)}</span>
                {item.to === "/admin/support" && supportCount > 0 && <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center shrink-0" data-testid="support-badge">{supportCount}</span>}
                {locked && <LockKey size={15} weight="fill" className="text-primary/70 shrink-0" data-testid={`nav-locked-${slug(item.to)}`} />}
              </NavLink>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

export function UserFooter({ email, onLogout }) {
  const { tr } = useLang();
  return (
    <div className="px-4 py-4 border-t border-border">
      <div className="flex items-center justify-between mb-3 gap-2">
        <p className="text-xs text-muted-foreground truncate" data-testid="current-user-email">{email}</p>
        <LanguageToggle />
      </div>
      <Button variant="secondary" data-testid="logout-button" onClick={onLogout} className="w-full justify-start gap-2 font-display uppercase tracking-wide">
        <SignOut size={18} weight="bold" /> {tr("Esci", "Sign out")}
      </Button>
    </div>
  );
}
